import { PlaycastPlayerInputAccess } from '@playcastdotio/CoreTypes';
import { getStateStore } from '../../stores';
import { ClientType, generators, LobbyMessageType, PresenceMessageType, websocket, MatchmakingMessageType, campaignApiResponseSchema, ProcessLaunchPackage } from '@playcastdotio/Messaging';
import { sendMessageToPlayer } from '../peers/host';
import { removeAssignmentsForPlayer } from './gamepadEvents';
import { sendMessage as hostRealtimeSend } from '../realtime';
import { clearActivityListener, checkPlayerActivity } from '../managers/playerActivity';
import { checkSessionTimeout } from './hostEvents';
import { invitationStateV2, playerInfoState, adModeState, hostState, matchmakerState, playjectorHostModeState, machineIdState, machineNameState, authState, hostCampaignConfigState, hostMatchmakingState, hostMacroConfigState } from '../../stores/shared';
import { getIpcFunctions, setWindowState } from '../ipcInterface';
import { burstMatchmakingDiagnostics } from '../diagnostics/matchmakingDiagnostics';
import { USE_SESSION_COORDINATOR, USE_MESSAGE_ROUTER, USE_PLAYJECTOR_COORDINATOR, USE_MEDIA_COORDINATOR } from '../../../config/featureFlags';
import { peerCoordinator } from '../../../coordinators/init';
import * as Sentry from '@sentry/react';
import constants from '@playcastdotio/GlobalConfig';
import { ipcSend } from '../../messaging/protocols/ipc';
import { campaignSwitchTracker, isTransientCampaignSwitchAbort } from '../managers/campaignSwitchTracker';
import { sendMatchmakerHostConfig } from '../managers/matchmaker';
import { attachSentryTraceToMessage } from '../realtime/tracing';
import { getHostCorrelationIds } from '../realtime/hostCorrelation';
import { addObservabilityHeaders } from '@playcastdotio/Observability';
import { sendAnalytics } from '@playcastdotio/Analytics';
import config from '../../../config.json';
import { sendLaunchMacro, sendPlayerExitMacro } from '../managers/macroManager';
import { serviceContainer } from '../../../services/ServiceContainer';

const state = getStateStore('ipc');

// Session timeout variables
let sessionTimeoutTimer: NodeJS.Timeout | undefined;

// Ad Mode: Game launch health check timeout (90 seconds)
let gameLaunchHealthCheckTimer: NodeJS.Timeout | undefined;
const DEFAULT_SESSION_TIMEOUT = 300000; // 5 minute default timeout (in ms)

// Update the combined playerInfoState with current player access and gamepad slots
const updatePlayerInfoState = () => {
  const playerState = state.playerState.currentState;
  const gamepadState = state.gamepadState.currentState;

  const updatedPlayers: Record<string, { access: PlaycastPlayerInputAccess; gamepadSlots: number[]; displayName: string }> = {};

  // Iterate through all players and combine their access and gamepad slot info
  Object.keys(playerState.players).forEach((playerId) => {
    const playerAccess = playerState.players[playerId].access;
    const displayName = playerState.players[playerId].displayName;
    const gamepadSlots = gamepadState.playerMappings[playerId]?.gamepadIndexes || [];

    updatedPlayers[playerId] = {
      access: playerAccess,
      gamepadSlots: gamepadSlots,
      displayName: displayName,
    };
  });

  playerInfoState.updateState({
    players: updatedPlayers,
  });
};

const waitForGameLaunchedFalse = (timeoutMs: number) => {
  let listener: string | undefined;
  return Promise.race([
    new Promise<boolean>((resolve) => {
      if (playjectorHostModeState.currentState?.gameLaunched === false) {
        resolve(true);
        return;
      }

      listener = playjectorHostModeState.addListener((state) => {
        if (state?.gameLaunched === false) {
          resolve(true);
        }
      });
    }),
    new Promise<boolean>((resolve) => setTimeout(() => resolve(false), timeoutMs)),
  ]).finally(() => {
    if (listener) playjectorHostModeState.removeListener(listener);
  });
};

export const initializePlayerEvents = () => {
  // Send updated access values to all players when they change
  let cachedPlayerState = { ...state.playerState.currentState.players };

  let playerCount = Object.keys(cachedPlayerState).length;
  state.playerState.addListener((playerState) => {
    // INPUT ACCESS CHANGES
    const wasAccessChanged = (access: PlaycastPlayerInputAccess, cachedAccess: PlaycastPlayerInputAccess): boolean => {
      if (cachedAccess === undefined) return true;
      return Object.keys(access).some((key) => {
        return access[key] !== cachedAccess[key];
      });
    };
    const changedInputPlayers = Object.keys(playerState.players).filter((key) => {
      return wasAccessChanged(playerState.players[key].access, cachedPlayerState[key]?.access);
    });
    if (changedInputPlayers.length > 0) {
      console.debug('[Player] Changed input access', changedInputPlayers);
      // if their gamepad state changed to false, remove their assignments
      changedInputPlayers.forEach((playerId) => {
        if (!playerState.players[playerId].access.gamepad) {
          removeAssignmentsForPlayer(playerId);
        }

        if (playerState.players[playerId].access.keyboard !== cachedPlayerState[playerId]?.access.keyboard) {
          clearActivityListener(playerId, 'keyboard');
        }
        if (playerState.players[playerId].access.mouse !== cachedPlayerState[playerId]?.access.mouse) {
          clearActivityListener(playerId, 'mouse');
        }
        if (playerState.players[playerId].access.gamepad !== cachedPlayerState[playerId]?.access.gamepad) {
          clearActivityListener(playerId, 'gamepad', undefined);
        }
      });
    }
    for (const playerId of changedInputPlayers) {
      sendMessageToPlayer(
        playerId,
        generators.player.setInputAccess(state.authState.currentState.hostAuthId, playerState.players[playerId].access),
      );
    }
    const currentPlayerCount = Object.keys(playerState.players).length;
    if (playerCount !== currentPlayerCount) {
      // update the player count
      debouncePlayerCount();

      // Check if we need to start or stop the timeout timer
      checkSessionTimeout(currentPlayerCount);

      // Log when first player joins after a match (replaces Match Found event)
      if (playerCount === 0 && currentPlayerCount > 0 && adModeState.currentState.enabled) {
        const invitationId = invitationStateV2.currentState.id;
        if (invitationId) {
          console.log('[MATCHMAKING] ✅ Match Found and Player Joined:', {
            invitationId,
            playerCount: currentPlayerCount,
          });

          Sentry.captureMessage('[MATCHMAKING] Match Found and Player Joined', {
            level: 'info',
            tags: {
              event_type: 'match_found_player_joined',
              invitation_id: invitationId,
              guest_mode: String(hostState.currentState.edition === 'pavilion' && state.hostMatchmakingState.currentState.guestAccount),
              ad_mode: String(adModeState.currentState.enabled),
              machine_id: machineIdState.currentState || 'unknown',
              machine_name: machineNameState.currentState || 'unknown',
              version: constants.VERSION,
            },
            contexts: {
              match: {
                invitationId,
                invitationType: invitationStateV2.currentState.type,
                playerCount: currentPlayerCount,
                machineId: machineIdState.currentState,
                machineName: machineNameState.currentState,
              },
              guest_mode: {
                enabled: state.hostMatchmakingState.currentState.guestAccount,
                edition: hostState.currentState.edition,
                adModeEnabled: adModeState.currentState.enabled,
                gameLaunched: playjectorHostModeState.currentState.gameLaunched,
                userLoggedIn: playjectorHostModeState.currentState.userLoggedIn,
              },
            },
          });
        }
      }

      // Log when last player leaves (session ends)
      if (playerCount > 0 && currentPlayerCount === 0 && adModeState.currentState.enabled) {
        const invitationId = invitationStateV2.currentState.id;
        if (invitationId) {
          console.log('[MATCHMAKING] ⚠️ All Players Left - Session Ended:', {
            invitationId,
            previousPlayerCount: playerCount,
          });

          Sentry.captureMessage('[MATCHMAKING] All Players Left - Session Ended', {
            level: 'info',
            tags: {
              event_type: 'all_players_left',
              invitation_id: invitationId,
              guest_mode: String(hostState.currentState.edition === 'pavilion' && state.hostMatchmakingState.currentState.guestAccount),
              ad_mode: String(adModeState.currentState.enabled),
              machine_id: machineIdState.currentState || 'unknown',
              machine_name: machineNameState.currentState || 'unknown',
              version: constants.VERSION,
            },
            contexts: {
              session: {
                invitationId,
                invitationType: invitationStateV2.currentState.type,
                previousPlayerCount: playerCount,
                machineId: machineIdState.currentState,
                machineName: machineNameState.currentState,
              },
              guest_mode: {
                enabled: state.hostMatchmakingState.currentState.guestAccount,
                edition: hostState.currentState.edition,
                adModeEnabled: adModeState.currentState.enabled,
                gameLaunched: playjectorHostModeState.currentState.gameLaunched,
                userLoggedIn: playjectorHostModeState.currentState.userLoggedIn,
              },
            },
          });
        }
      }

      // Ad Mode: Clear health check timer when player joins (prevents false positive after players have connected)
      if (
        hostState.currentState.edition === 'pavilion' &&
        adModeState.currentState.enabled &&
        playerCount === 0 &&
        currentPlayerCount > 0 &&
        gameLaunchHealthCheckTimer
      ) {
        console.log('[Ad Mode] ✅ Player joined - game launch successful (clearing health check timer)');

        // Success case - no need to log to Sentry, player joined successfully
        // Health check passed, clear the timeout
        clearTimeout(gameLaunchHealthCheckTimer);
        gameLaunchHealthCheckTimer = undefined;
      }

      // Ad Mode: Handle player disconnect (with optional restart)
      // STRANGLER-FIG: Skip legacy restart flow when coordinators own matchmaking/session lifecycle
      if (
        !USE_MESSAGE_ROUTER &&
        hostState.currentState.edition === 'pavilion' &&
        adModeState.currentState.enabled &&
        playerCount > 0 &&
        currentPlayerCount === 0
      ) {
        console.log('[Ad Mode] Player count dropped to 0, cleaning up session');

        // Invalidate the current matchmaking session (host-specific message)
        if (matchmakerState.currentState.stateDetails?.currentMatchInvitationId) {
          console.log('[Ad Mode] Invalidating matchmaking session:', matchmakerState.currentState.stateDetails.currentMatchInvitationId);
          hostRealtimeSend(
            websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_INVALIDATE_SESSION, {
              invitationId: matchmakerState.currentState.stateDetails.currentMatchInvitationId,
              reason: 'Ad Mode: Player count dropped to 0',
            }),
          );
        }

        // CRITICAL: Capture invitation ID BEFORE any state changes
        // This is used later by health check to detect stale sessions
        const currentMatchInvitationId = matchmakerState.currentState.stateDetails?.currentMatchInvitationId;

        // CRITICAL: Capture campaign config BEFORE async operations to prevent race conditions
        const restartCampaignId = hostCampaignConfigState.currentState.campaignId;

        // Fetch campaign config and decide whether to restart - wrap in async IIFE to use await
        (async () => {
          if (!restartCampaignId) {
            console.error('[Ad Mode] No campaign configured - becoming idle');
            hostMatchmakingState.updateState({
              ...hostMatchmakingState.currentState,
              campaignId: undefined,
              gameState: 'idle',
              // Don't set matchmakeable - let state machine calculate it
              // (Ad Mode will be unmatchmakeable until a game launches)
            });
            // TODO-STRANGLER-FIG: Legacy HOST_CONFIG send - remove when PavilionCoordinator fully owns this
            if (!USE_MESSAGE_ROUTER) {
              await sendMatchmakerHostConfig();
            }
            return;
          }

          // Fetch campaign config from realtime-api to check restartOnDisconnect setting
          console.log(`[Ad Mode] Fetching config for ${restartCampaignId}...`);
          const baseUrl = constants.REALTIME_API_BASE_URL.replace(/\/$/, ''); // Remove trailing slash
          const apiUrl = `${baseUrl}/realtime/campaigns/${restartCampaignId}/host-config`;

          const ids = getHostCorrelationIds();
          const headers = addObservabilityHeaders(new Headers(), () => ids.machineId, () => ids.broadcastId);
          const response = await fetch(apiUrl, { headers });
          if (!response.ok) {
            throw new Error(`Failed to fetch campaign config: ${response.statusText}`);
          }

          const rawData = await response.json();
          const data = campaignApiResponseSchema.parse(rawData);
          if (!data.success || !data.campaign) {
            throw new Error(data.error || 'Campaign not found in response');
          }

          const campaignConfig = data.campaign;
          const campaignDisplayName = restartCampaignId; // Use campaignId directly (launchPackage doesn't have displayName)

          // Update campaign config state with restartOnDisconnect setting
          hostCampaignConfigState.updateState({
            ...hostCampaignConfigState.currentState,
            restartOnDisconnect: (campaignConfig as any).restartOnDisconnect,
          });

          hostMacroConfigState.updateState({
            onLaunchMacro: campaignConfig.onLaunchMacro,
            onJoinMacro: campaignConfig.onJoinMacro,
            onExitMacro: (campaignConfig as any).onExitMacro ?? [],
            onInactiveMacro: (campaignConfig as any).onInactiveMacro ?? [],
          });

          const restartLaunchPackage = campaignConfig.launchPackage;
          if (!restartLaunchPackage) {
            throw new Error('No launch package found for campaign');
          }

          if (restartLaunchPackage.launcher === 'epic') {
            if (!restartLaunchPackage.itemId) {
              throw new Error('Missing required Epic itemId field');
            }
          }

          // Check if restart is enabled (defaults to true for backward compatibility)
          const shouldRestart = (campaignConfig as any).restartOnDisconnect !== false;

          if (!shouldRestart) {
            // Restart disabled: Keep game running and let state machine handle matchmakeable status
            console.log('[Ad Mode] ✅ Restart disabled - game will continue running');

            // TODO-STRANGLER-FIG: Legacy exit macro call - disabled when coordinator handles macros
            if (!USE_PLAYJECTOR_COORDINATOR) {
              await sendPlayerExitMacro();
            }

            // CRITICAL: Cancel timer and stop stream BEFORE resetting matchmaker
            // This prevents stale presence updates
            if (timer) {
              clearTimeout(timer);
              timer = undefined;
            }
            const ipcFuncs = getIpcFunctions();
            ipcFuncs.stopStream('ad_mode:no_players_no_restart');
            console.log('[Ad Mode] Stream stopped (game continues running)');

            // CRITICAL: Reset matchmaker state to clear invitation tracking
            // Safe to do now since we're not restarting (no prewarming state to protect)
            matchmakerState.reset();
            console.log('[Ad Mode] Reset matchmaker state (cleared invitation tracking)');

            // CRITICAL: Notify API immediately that host is ready for matchmaking
            // Without this, the API would only learn about the host's availability after
            // the next periodic recalculation (up to 10 seconds delay)
            console.log('[Ad Mode] Notifying API of host availability...');
            await sendMatchmakerHostConfig();
            console.log('[Ad Mode] Host marked as available for matchmaking');
            // The state machine will recalculate matchmakeable based on:
            // - gameLaunched: true (game still running)
            // - no active match (we just reset matchmakerState)
            // - adMode enabled
            // → Host should be matchmakeable again immediately

            // Log to Sentry for tracking
            Sentry.addBreadcrumb({
              category: 'ad_mode',
              message: 'Restart disabled - game continues running after player disconnect',
              level: 'info',
              data: {
                campaignId: restartCampaignId,
                machineId: machineIdState.currentState,
                restartOnDisconnect: false,
              },
            });

            return; // Exit early - no restart needed
          }

          // Restart enabled: Proceed with full restart flow
          console.log('[Ad Mode] Restart enabled - will restart game process');
          console.log('[Ad Mode] Starting burst diagnostic logging');
          // burstMatchmakingDiagnostics('ad_mode_process_restart', 20);

          // CRITICAL: Set prewarming state IMMEDIATELY (before cleanup)
          // This prevents the host from being matched during the restart window
          hostMatchmakingState.updateState({
            ...hostMatchmakingState.currentState,
            gameState: 'prewarming',
            matchmakeable: false, // Not available during restart
          });

          // TODO-STRANGLER-FIG: Legacy HOST_CONFIG send - remove when PavilionCoordinator fully owns this
          if (!USE_MESSAGE_ROUTER) {
            console.log('[Ad Mode] Notifying API of prewarming state...');
            await sendMatchmakerHostConfig();
          }

          // CRITICAL: Safety timeout to prevent stuck prewarming state
          // Calculate as primary timeout + buffer to let primary timeout fire first
          const primaryTimeout = config.CAMPAIGN_SWITCH_TIMEOUT + (restartLaunchPackage?.readyWaitTimeMs || 0);
          // const PREWARMING_SAFETY_BUFFER = 5 * 60 * 1000; // 5 minutes buffer
          const PREWARMING_SAFETY_TIMEOUT = primaryTimeout; // + PREWARMING_SAFETY_BUFFER;
          console.log(`[Ad Mode] ⏱️ Setting safety timeout: ${PREWARMING_SAFETY_TIMEOUT}ms (primary: ${primaryTimeout}ms)`);

          const prewarmingSafetyTimer: NodeJS.Timeout = setTimeout(() => {
            // Check if still in prewarming (restart didn't complete)
            if (hostMatchmakingState.currentState.gameState === 'prewarming') {
              console.error(`[Ad Mode] ⚠️ SAFETY TIMEOUT: Host stuck in prewarming for ${PREWARMING_SAFETY_TIMEOUT}ms, forcing cleanup`);

              // Report permanent failure to API
              // This prevents capacity manager from repeatedly trying this broken campaign
              const failureMessage = websocket.generators.matchmaking(
                MatchmakingMessageType.MATCHMAKER_CAMPAIGN_SWITCH_FAILURE,
                {
                  campaignId: restartCampaignId,
                  reason: 'restart_safety_timeout',
                  machineName: machineIdState.currentState,
                  transient: false, // Permanent failure - something is broken
                }
              );
              hostRealtimeSend(attachSentryTraceToMessage(failureMessage));
              console.log('[Ad Mode] Sent safety timeout failure report to realtime-API');

              // Force cleanup to idle state
              hostMatchmakingState.updateState({
                ...hostMatchmakingState.currentState,
                campaignId: undefined,
                gameState: 'idle',
              });

              hostCampaignConfigState.updateState({
                ...hostCampaignConfigState.currentState,
                campaignId: undefined,
                launchPackage: undefined,
                restartOnDisconnect: undefined,
              });

              // TODO-STRANGLER-FIG: Legacy HOST_CONFIG send - remove when PavilionCoordinator fully owns this
              if (!USE_MESSAGE_ROUTER) {
                sendMatchmakerHostConfig().catch(err =>
                  console.error('[Ad Mode] Failed to send HOST_CONFIG after safety timeout:', err)
                );
              }

              // Report to Sentry
              Sentry.captureMessage('[Ad Mode] Host stuck in prewarming - safety timeout triggered', {
                level: 'error',
                tags: {
                  event_type: 'ad_mode_prewarming_safety_timeout',
                  campaign_id: restartCampaignId,
                  timeout_ms: PREWARMING_SAFETY_TIMEOUT,
                },
                contexts: {
                  restart: {
                    campaignId: restartCampaignId,
                    primaryTimeout: primaryTimeout,
                    safetyTimeout: PREWARMING_SAFETY_TIMEOUT,
                    machineId: machineIdState.currentState,
                  },
                },
              });
            }
          }, PREWARMING_SAFETY_TIMEOUT);

          // CRITICAL FIX: Cancel any pending debounce timer before stopping stream
          // Without this, the debounce timer could fire after stopStream, sending a
          // stale presence update with status='hosting' that overwrites the status='idle'
          // update from stopStream, causing the "hosting with 0 players" stuck state.
          if (timer) {
            clearTimeout(timer);
            timer = undefined;
          }

          // Stop the stream and terminate the game process
          // Note: We await this to ensure presence is updated before proceeding
          const ipcFuncs = getIpcFunctions();
          ipcFuncs.stopStream('ad_mode:no_players_immediate_restart');

          // CRITICAL: Terminate the game process before restarting
          // Without this, Playjector may fail to launch because the old process is still running
          console.log('[Ad Mode] Terminating game process...');
          ipcSend(generators.process.processTerminate(
            authState.currentState.hostAuthId,
            'pavilion',
          ));

        // CRITICAL FIX: Use proper timeout tracking for game restarts
        // Previously this was fire-and-forget, which could leave the host stuck forever
        // if the game failed to launch. Now we use campaignSwitchTracker for proper
        // timeout protection, failure reporting, and state cleanup.
        console.log('[Ad Mode] Restarting game process in 10 seconds');
        setTimeout(async () => {
          try {
            const campaignId = restartCampaignId;
            const launchPackage = restartLaunchPackage;

            console.log('[Ad Mode] Executing game restart for campaign:', restartCampaignId);

            // Calculate timeout: base timeout + game-specific ready wait time
            const totalTimeoutMs = config.CAMPAIGN_SWITCH_TIMEOUT + launchPackage.readyWaitTimeMs;
            console.log(`[Ad Mode] ⏱️ Setting restart timeout: ${totalTimeoutMs}ms (base: ${config.CAMPAIGN_SWITCH_TIMEOUT}ms + ready: ${launchPackage.readyWaitTimeMs}ms)`);

            // Start tracking the restart with timeout protection
            const launchResultPromise = campaignSwitchTracker.startSwitch(
              restartCampaignId,
              totalTimeoutMs,
            );

            console.log('[Ad Mode] 🎮 Sending game restart request to Playjector');
            ipcSend(generators.process.processLaunch(
              authState.currentState.hostAuthId,
              'pavilion',
              launchPackage as ProcessLaunchPackage,
            ));

            // Wait for pavilionLaunchReply (or timeout)
            console.log('[Ad Mode] Waiting for pavilionLaunchReply...');
            const launchResult = await launchResultPromise;

            if (launchResult.success) {
              // SUCCESS: Game restarted successfully
              console.log('[Ad Mode] ✅ Game restarted successfully - ready for next session');

              // Clear safety timeout
              clearTimeout(prewarmingSafetyTimer);

              // TODO-STRANGLER-FIG: Legacy macro call - disabled when coordinator handles macros
              if (!USE_PLAYJECTOR_COORDINATOR) {
                await sendLaunchMacro();
              }

              // Update host state explicitly (don't rely on implicit Playjector state updates)
              hostMatchmakingState.updateState({
                ...hostMatchmakingState.currentState,
                gameState: 'ready',
                matchmakeable: true, // Back in matchmaking pool
                lastSwitchedToCampaign: Date.now(), // Update restart timestamp
              });

              // Reset matchmaker state (clear invitation tracking)
              matchmakerState.reset();

              // TODO-STRANGLER-FIG: Legacy HOST_CONFIG send - remove when PavilionCoordinator fully owns this
              if (!USE_MESSAGE_ROUTER) {
                // Notify API of ready state
                await sendMatchmakerHostConfig();
              }

              console.log('[Ad Mode] Host is now ready and matchmakeable');

            } else {
              // FAILURE: Game failed to restart or timed out
              const failureReason = launchResult.reason;

              // Clear safety timeout
              clearTimeout(prewarmingSafetyTimer);

              if (isTransientCampaignSwitchAbort(launchResult)) {
                console.warn(
                  `[Ad Mode] Restart aborted (${failureReason}) — superseded or cancelled; skipping permanent failure / state wipe`,
                );
                return;
              }

              console.error('[Ad Mode] ❌ Game restart failed:', failureReason);

              // Report permanent failure to API
              // This prevents capacity manager from repeatedly trying this broken campaign
              const failureMessage = websocket.generators.matchmaking(
                MatchmakingMessageType.MATCHMAKER_CAMPAIGN_SWITCH_FAILURE,
                {
                  campaignId,
                  reason: `restart_failed_${failureReason}`,
                  machineName: machineIdState.currentState,
                  transient: false, // Permanent failure - game is broken
                }
              );
              hostRealtimeSend(attachSentryTraceToMessage(failureMessage));
              console.log('[Ad Mode] Sent permanent failure report to realtime-API');

              // Clear campaign state - host cannot serve this campaign anymore
              hostMatchmakingState.updateState({
                ...hostMatchmakingState.currentState,
                campaignId: undefined,
                gameState: 'idle',
                // Don't set matchmakeable - let state machine calculate it
                // (Ad Mode will be unmatchmakeable until a game launches)
              });

              hostCampaignConfigState.updateState({
                ...hostCampaignConfigState.currentState,
                campaignId: undefined,
                launchPackage: undefined,
                restartOnDisconnect: undefined,
              });

              // TODO-STRANGLER-FIG: Legacy HOST_CONFIG send - remove when PavilionCoordinator fully owns this
              if (!USE_MESSAGE_ROUTER) {
                // Notify API of state change
                await sendMatchmakerHostConfig();
              }

              // Send failure analytics (using existing campaign switch failed event)
              serviceContainer.analyticsManager.sendSwitchCampaignFailedEvent({
                campaignId,
                previousCampaignId: campaignId, // Same campaign - this is a restart
                reason: `restart_${failureReason}`,
                gameName: campaignDisplayName, // Use captured name (state is already cleared)
                launcher: launchPackage.launcher,
              });

              // Log to Sentry
              Sentry.captureMessage(`[Ad Mode] Game restart failed after player left`, {
                level: 'error',
                tags: {
                  event_type: 'ad_mode_restart_failure',
                  campaign_id: campaignId,
                  reason: failureReason,
                },
                contexts: {
                  ad_mode_restart: {
                    campaignId,
                    reason: failureReason,
                    machineId: machineIdState.currentState,
                    previousInvitationId: currentMatchInvitationId,
                  },
                },
              });
            }

          } catch (error) {
            console.error('[Ad Mode] ❌ Exception during game restart:', error);

            // Clear safety timeout
            clearTimeout(prewarmingSafetyTimer);

            // Cancel any pending tracking
            campaignSwitchTracker.cancelSwitch();

            // Clear state and become idle
            hostMatchmakingState.updateState({
              ...hostMatchmakingState.currentState,
              campaignId: undefined,
              gameState: 'idle',
              // Don't set matchmakeable - let state machine calculate it
              // (Ad Mode will be unmatchmakeable until a game launches)
            });

            hostCampaignConfigState.updateState({
              ...hostCampaignConfigState.currentState,
              campaignId: undefined,
              launchPackage: undefined,
              restartOnDisconnect: undefined,
            });

            // TODO-STRANGLER-FIG: Legacy HOST_CONFIG send - remove when PavilionCoordinator fully owns this
            if (!USE_MESSAGE_ROUTER) {
              await sendMatchmakerHostConfig();
            }

            Sentry.captureException(error, {
              tags: {
                event_type: 'ad_mode_restart_exception',
              },
            });
          }
        }, 10000);
        })().catch((error) => {
          console.error('[Ad Mode] ❌ Error in restart initialization:', error);
          Sentry.captureException(error, {
            tags: {
              event_type: 'ad_mode_restart_init_error',
            },
          });
        });

        const DISABLE_GAME_LAUNCH_HEALTH_CHECK = true;
        // Start 2min30s health check for game launch
        // After 2 minutes 30 seconds, check if game launched and send appropriate metric
        console.log('[Ad Mode] Starting 2min30s game launch health check');
        if (gameLaunchHealthCheckTimer) {
          clearTimeout(gameLaunchHealthCheckTimer);
        }

        if (!DISABLE_GAME_LAUNCH_HEALTH_CHECK) {
          gameLaunchHealthCheckTimer = setTimeout(() => {
            const isGameLaunched = playjectorHostModeState.currentState.gameLaunched;
            const machineId = machineIdState.currentState || 'unknown';
            const machineName = machineNameState.currentState || 'unknown';

            // Safety check: Verify matchmaker recovered properly after restart
            // After 90s with no players, matchmaker should be in REGISTERED state (back in pool)
            // If not REGISTERED, we have a stale/stuck session that needs cleanup
            const currentPlayerCount = Object.keys(state.playerState.currentState.players).length;
            const matchmakerStateValue = matchmakerState.currentState.state;
            const currentMatchmakerInvitation = matchmakerState.currentState.stateDetails?.currentMatchInvitationId;

            // Primary detection: matchmaker should be REGISTERED after game restart with no players
            // IDLE = not meeting criteria (game not launched)
            // REGISTERED = in matchmaking pool, ready for matches ✅
            // MATCHMAKING_DISABLED with 0 players = could be stale OR new match with players still connecting
            if (currentPlayerCount === 0 && matchmakerStateValue !== 'REGISTERED') {
              // Check if this is a NEW match (invitation ID changed) or stale session (same ID)
              const isNewMatch = currentMatchmakerInvitation && currentMatchmakerInvitation !== currentMatchInvitationId;

              if (isNewMatch) {
                // This is a NEW match that came in after restart - players may still be connecting
                console.log('[Ad Mode] ✅ New match detected after restart - invitation ID changed');
                console.log('[Ad Mode] Old invitation:', currentMatchInvitationId);
                console.log('[Ad Mode] New invitation:', currentMatchmakerInvitation);
                console.log('[Ad Mode] State:', matchmakerStateValue, '- waiting for players to connect');
                // Don't invalidate - this is a legitimate new match
              } else {
                // Stale session detected - same invitation ID or no invitation
                console.warn('[Ad Mode] ⚠️ Matchmaker not REGISTERED after 90s - stale session detected');
                console.warn('[Ad Mode] Current matchmaker state:', matchmakerStateValue);
                console.warn('[Ad Mode] Player count:', currentPlayerCount);
                console.warn('[Ad Mode] Old invitation ID:', currentMatchInvitationId);
                console.warn('[Ad Mode] Current invitation ID:', currentMatchmakerInvitation);

                // Send Sentry event to track this issue
                Sentry.captureMessage('Ad Mode: Stale Matchmaker Session Detected', {
                  level: 'warning',
                  tags: {
                    issue_type: 'stale_matchmaker_session',
                    matchmaker_state: matchmakerStateValue,
                    machine_id: machineId,
                    machine_name: machineName,
                    game_launched: isGameLaunched.toString(),
                    version: constants.VERSION,
                  },
                  extra: {
                    matchmakerState: matchmakerStateValue,
                    playerCount: currentPlayerCount,
                    gameLaunched: isGameLaunched,
                    oldInvitationId: currentMatchInvitationId,
                    currentInvitationId: currentMatchmakerInvitation,
                    adModeEnabled: adModeState.currentState.enabled,
                    steamId: adModeState.currentState.steamId,
                    machineId,
                    machineName,
                    version: constants.VERSION,
                    timestamp: Date.now(),
                    context: 'After 90s game restart, matchmaker should be REGISTERED but is not, and invitation ID unchanged',
                  },
                });

                // If we captured the old invitation ID, retry invalidation to clean up remote API
                if (currentMatchInvitationId) {
                  console.warn('[Ad Mode] Retrying invalidation for old invitation:', currentMatchInvitationId);
                  hostRealtimeSend(
                    websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_INVALIDATE_SESSION, {
                      invitationId: currentMatchInvitationId,
                      reason: 'Ad Mode: Retry invalidation - stale session with same invitation ID',
                    }),
                  );
                }

                // Force reset local matchmaker state to recover
                console.log('[Ad Mode] Force resetting matchmaker state to recover');
                matchmakerState.reset();
              }
            }

            if (isGameLaunched) {
              // ✅ SUCCESS: Game launched within 2min30s
              console.log('[Ad Mode] ✅ Game launch health check PASSED - game launched within 2min30s');
              // Success case - no need to log to Sentry, game launched successfully
            } else {
              // ❌ FAILURE: Game did not launch within 2min30s
              console.error('[Ad Mode] ❌ Game launch health check FAILED - game did not launch within 2min30s');

              Sentry.captureMessage('Ad Mode: Game Launch Health Check Failed', {
                level: 'error',
                tags: {
                  check_type: 'game_launch_health',
                  result: 'failed',
                  timeout_seconds: '90',
                  machine_id: machineId,
                  machine_name: machineName,
                  version: constants.VERSION,
                },
                extra: {
                  gameLaunched: isGameLaunched,
                  adModeEnabled: adModeState.currentState.enabled,
                  steamId: adModeState.currentState.steamId,
                  machineId,
                  machineName,
                  version: constants.VERSION,
                  timestamp: Date.now(),
                },
              });

              // Close the app
              console.log('[Ad Mode] Closing application due to game launch failure');
              // Log new event to Sentry
              Sentry.captureMessage('Guest Mode Host App Closed', {
                level: 'error',
                tags: {
                  event_type: 'guest_mode_host_app_closed_from_game_launch_health_check',
                  guest_mode: String(hostState.currentState.edition === 'pavilion' && state.hostMatchmakingState.currentState.guestAccount),
                  ad_mode: String(adModeState.currentState.enabled),
                  machine_id: machineIdState.currentState || 'unknown',
                  machine_name: machineNameState.currentState || 'unknown',
                  version: constants.VERSION,
                },
                extra: {
                  gameLaunched: isGameLaunched,
                  adModeEnabled: adModeState.currentState.enabled,
                  steamId: adModeState.currentState.steamId,
                  machineId,
                  machineName,
                  version: constants.VERSION,
                  timestamp: Date.now(),
                },
              })
              setWindowState('closed');
            }

            gameLaunchHealthCheckTimer = undefined;
          }, 150000); // 2 minute 30 seconds
        }
      }
    }

    // VIDEO STATE CHANGES
    // for (const playerId of Object.keys(playerState.players)) {
    //   setVideo(playerId);
    // }
    // STRANGLER-FIG: Bridge player join/leave to PeerCoordinator
    if (USE_MEDIA_COORDINATOR) {
      // Detect added players
      const addedPlayers = Object.keys(playerState.players).filter((p) => !Object.keys(cachedPlayerState).includes(p));
      addedPlayers.forEach((playerId) => {
        const player = playerState.players[playerId];
        peerCoordinator.onPlayerJoined(playerId, player.displayName || 'Unknown Player');
      });

      // Detect removed players
      const removedPlayers = Object.keys(cachedPlayerState).filter((playerId) => {
        return !Object.keys(playerState.players).includes(playerId);
      });
      removedPlayers.forEach((playerId) => {
        peerCoordinator.onPlayerLeft(playerId);
      });
    }

    const invitation = state.invitationStateV2.currentState;
    if (invitation.type === 'lobby') {
      const addedPlayers = Object.keys(playerState.players).filter((p) => !Object.keys(cachedPlayerState).includes(p));
      addedPlayers.forEach((playerId) => {
        const player = playerState.players[playerId];
        const id = player.invitationPlayerId;
        if (!id) return;

        hostRealtimeSend(
          websocket.generators.lobby(LobbyMessageType.LOBBY_REMOVE_PENDING, {
            invitationId: invitation.id,
            playerId: id,
          }),
        );
      });
    }

    // find players that have disconnected
    const disconnectedPlayers = Object.keys(cachedPlayerState).filter((playerId) => {
      return !Object.keys(playerState.players).includes(playerId);
    });

    disconnectedPlayers.forEach((playerId) => {
      // clean up gamepad state
      removeAssignmentsForPlayer(playerId);
    });

    cachedPlayerState = { ...playerState.players };
    playerCount = currentPlayerCount;

    // Update the combined playerInfoState whenever player state changes
    updatePlayerInfoState();
  });

  state.playerActivityState.addListener((playerActivityState) => {
    checkPlayerActivity();
    // if (playerActivityState.inactive && invitationStateV2.currentState.type === 'cafe') {
    //   // Stop the stream
    //   getIpcFunctions().stopStream('cafe_players_inactive');
    // }
  });
};

// Debounce player count updates to prevent spamming the API
const DEBOUNCE_TIME = 50;
let timer: NodeJS.Timeout | undefined;

const debouncePlayerCount = () => {
  // Clear any existing timer
  if (timer) {
    clearTimeout(timer);
  }

  // Set a new timer to fire the function after the debounce time
  timer = setTimeout(() => {
    timer = undefined; // Reset the timer
    const playerCount = Object.keys(state.playerState.currentState.players).length;
    console.warn('Sending player count (from debounced player event)', playerCount);

    // STRANGLER-FIG: Skip PRESENCE_UPDATE when SessionCoordinator is active
    if (!USE_SESSION_COORDINATOR) {
      hostRealtimeSend(
        websocket.generators.presence(PresenceMessageType.PRESENCE_UPDATE, {
          clientType: ClientType.HOST,
          presence: {
            ...state.hostPresenceState.currentState,
            playerCount,
          },
        }),
      );
    }

    state.hostPresenceState.updateState({
      ...state.hostPresenceState.currentState,
      playerCount,
    });
    return;
  }, DEBOUNCE_TIME);
};
